import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Flex } from 'antd';
import { RcFile } from 'antd/es/upload';
import { useFormik } from 'formik';
import dayjs from 'dayjs';

import {
  useGetBalanceMutation,
  usePostLeaveFormMutation,
} from '@Features/EmployeeSelfService/requests/LeaveRequest/store/api/leaveRequestApiSlice';
import { useFormUploadMutation } from '@Store/api/dmsApiSlice';
import { useSubmitHrmRequestMutation } from '@Store/api/basicHrmApiSlice';
import {
  useAppDispatch,
  useAppSelector,
  useNavigationToListRequests,
} from '@Hooks/index';
import { FooterActions, LeaveTypeCodes } from '@Enum/index';
import { currentUserSelector } from '@Store/selectors/userInfoSelector';
import {
  getValidationSettings,
  leaveRequestCreateRequisitionInfo,
} from '@Features/EmployeeSelfService/requests/LeaveRequest/store/selectors/leaveRequestSelector';
import { getLeaveTypeDictSelector } from '@Store/selectors/dictionarySelector';
import { getTotalDays, parseDateTime } from '@Utils/transformDateTime';
import { RcCustomRequestOptions } from '@Components/types/UploadType';
import {
  DocumentFormUploadResponse,
  LeaveRequestFormPostRequest,
  RequestError,
} from '@Types/api/form';
import {
  transformDictToOptions,
  transformFlagsToOptions,
} from '@Utils/transformToOptions';
import { leaveRequestActions } from '@Features/EmployeeSelfService/requests/LeaveRequest/store/leaveRequestSlice';
import { currentFileUploadSelector } from '@Store/selectors/fileUploadSelector';
import { fileUploadActions } from '@Store/slices/fileUploadSlice';
import { generateFilepath } from '@Utils/generateFilepath';
import { validateForm } from '@Features/utils/validateForm';
import { NotificationContext } from '@Context/NotificationContext';
import { AVAILABLE_BALANCE_DATE_FORMAT } from '@Features/EmployeeSelfService/requests/LeaveRequest/store/api/constants';
import { MainForm } from '@Components/index';
import FormItems from '@Components/MainForm/components/FormItems/FormItems';
import { Balance } from '@Types/api/form/leaveRequest/Balance';

import LeaveDetailsSection from '../components/LeaveDetailsSection/LeaveDetailsSection';
import { LeaveRequestFormValues } from '../type';
import { initialBalance, initialValuesForm } from '../initialValues';
import { descriptionAlert } from '../descriptionAlert';
import { getLeaveRequestFormSchema } from '../validationSchema';
import { getFormUploadFileMetaData } from '../utils/document';
import ReplacementDetailsSection from '../components/ReplacementDetailsSection/ReplacementDetailsSection';
import useReplacementManager from '../hooks/useReplacementManager';

const LeaveRequestCreateForm: React.FC = () => {
  const [skipGetReplacementUsers, setSkipGetReplacementUsers] = useState(true);
  const {
    saveSuccessNotification,
    errorNotification,
    submitSuccessNotification,
  } = useContext(NotificationContext);
  const leaveTypes = useAppSelector(getLeaveTypeDictSelector);
  const currentFileUpload = useAppSelector(currentFileUploadSelector);
  const requisitionInfo = useAppSelector(leaveRequestCreateRequisitionInfo);
  const validationSettings = useAppSelector(getValidationSettings);
  const [postLeaveForm] = usePostLeaveFormMutation();
  const [submitForm] = useSubmitHrmRequestMutation();
  const [formUpload] = useFormUploadMutation();
  const [getBalance] = useGetBalanceMutation();
  const currentUser = useAppSelector(currentUserSelector);
  const dispatch = useAppDispatch();
  const [disabledSaveForm, setDisableSaveForm] = useState(false);
  const [initialFormValues, setInitialFormValues] = useState(initialValuesForm);
  const [balance, setBalance] = useState<Balance>(initialBalance);
  const { handleBackToListRequests } = useNavigationToListRequests();
  const { replacementUsers, onLoadMore, onSearch } = useReplacementManager({
    skipGetReplacementUsers,
  });

  useEffect(
    () => () => {
      dispatch(leaveRequestActions.setLeaveRequest(null));
      dispatch(fileUploadActions.setFile(null));
    },
    []
  );

  useEffect(() => {
    if (!leaveTypes.length) {
      return;
    }

    setInitialFormValues({
      ...initialValuesForm,
      leaveTypeID: `${LeaveTypeCodes.ANNUAL_LEAVE}`,
    });
  }, [leaveTypes]);

  const handleFileRemove = useCallback((): void => {
    dispatch(fileUploadActions.setFile(null));
  }, []);

  const handleFileRequest = useCallback(
    (options: RcCustomRequestOptions): void => {
      const { file, onSuccess, onError } = options;
      const cb = (): void => {};
      const successCb = onSuccess || cb;
      const errorCb = onError || cb;
      const formData = new FormData();
      formData.append('file', file);

      if (file) {
        const filepath = generateFilepath(
          'LeaveRequest',
          (file as RcFile).name,
          currentUser?.empNumber
        );
        dispatch(
          fileUploadActions.setFile({
            uploadedFile: file as RcFile,
            formData,
            filepath,
          })
        );
        successCb('Ok');
      } else {
        errorCb(new Error('file not uploaded'));
      }
    },
    [currentUser]
  );

  const uploadFile = useCallback(
    (reqFileId: string): Promise<DocumentFormUploadResponse> => {
      const { formData, filepath } = currentFileUpload;

      const body = getFormUploadFileMetaData(reqFileId, filepath);

      return formUpload({
        document: JSON.stringify(body),
        body: formData,
      }).unwrap();
    },
    [currentFileUpload]
  );

  const handleSubmitForm = useCallback(
    (values: LeaveRequestFormValues) => {
      const submitFormHandler = async (): Promise<void> => {
        let body = {
          empNumber: currentUser?.empNumber,
          startDate: values.startDate,
          endDate: values.endDate,
          remarks: values.remarks,
          telephone: values.telephone,
          replacementEmpNo: values.replacementEmpNo || '',
          totDays: Number(values.totDays),
          leaveTypeID: Number(values.leaveTypeID),
          ticketRequired: Number(values.ticketRequired),
          passportRequired: Number(values.passportRequired),
          filepath: currentFileUpload.filepath || undefined,
          isReplacementRequired: values.isReplacementRequired,
        } as LeaveRequestFormPostRequest;

        if (
          Number(values.ticketRequired) &&
          Number(values.leaveTypeID) === LeaveTypeCodes.ANNUAL_LEAVE &&
          Number(values.ticketRequired) === 1
        ) {
          body = {
            ...body,
            totalTickets: balance.eligibleTickets ?? 0,
          };
        }

        if (requisitionInfo?.requisitionNo) {
          body = {
            ...body,
            requestId: Number(requisitionInfo?.requisitionNo),
          };
        }

        setDisableSaveForm(true);

        try {
          const {
            createdDate,
            reqStatusId,
            requestId: reqId,
            leaveTypeID,
          } = await postLeaveForm(body).unwrap();

          dispatch(
            leaveRequestActions.setLeaveRequest({
              createdDate,
              requestId: reqId,
              reqStatusId,
            })
          );

          await submitForm(String(reqId)).unwrap();

          if (leaveTypeID === LeaveTypeCodes.SICK_LEAVE) {
            await uploadFile(String(reqId));
          }

          submitSuccessNotification(String(reqId));

          setDisableSaveForm(false);

          handleBackToListRequests();
        } catch (error) {
          const message = (error as RequestError)?.data;
          errorNotification(message);

          setDisableSaveForm(false);
        }
      };

      submitFormHandler();
    },
    [
      handleBackToListRequests,
      requisitionInfo,
      currentFileUpload,
      uploadFile,
      currentUser,
    ]
  );

  const validationSchema = useMemo(() => {
    const isFileAttached = !!currentFileUpload.filepath;
    const { isStaff } = currentUser;
    const output = getLeaveRequestFormSchema(
      isFileAttached,
      isStaff,
      validationSettings
    );
    return output;
  }, [currentFileUpload]);

  const formik = useFormik({
    initialValues: initialFormValues,
    enableReinitialize: true,
    onSubmit: handleSubmitForm,
    validationSchema,
  });

  useEffect(() => {
    // setting value to trigger validation
    const timeStamp = dayjs().toISOString();
    formik.setFieldValue('document', timeStamp);
  }, [currentFileUpload.filepath]);

  const handleSaveAsDraft = useCallback((): void => {
    const saveAsDraft = async (): Promise<void> => {
      let body = {
        empNumber: currentUser?.empNumber,
        startDate: formik.values.startDate,
        endDate: formik.values.endDate,
        remarks: formik.values.remarks,
        telephone: formik.values.telephone,
        replacementEmpNo: formik.values.replacementEmpNo || '',
        totDays: Number(formik.values.totDays),
        leaveTypeID: Number(formik.values.leaveTypeID),
        ticketRequired: Number(formik.values.ticketRequired),
        passportRequired: Number(formik.values.passportRequired),
        filepath: currentFileUpload.filepath || undefined,
        isReplacementRequired: formik.values.isReplacementRequired,
      } as LeaveRequestFormPostRequest;

      if (
        Number(formik.values.ticketRequired) &&
        Number(formik.values.leaveTypeID) === LeaveTypeCodes.ANNUAL_LEAVE &&
        Number(formik.values.ticketRequired) === 1
      ) {
        body = {
          ...body,
          totalTickets: balance.eligibleTickets ?? 0,
        };
      }

      setDisableSaveForm(false);

      try {
        const {
          requestId: reqId,
          createdDate,
          reqStatusId,
          leaveTypeID,
        } = await postLeaveForm(body).unwrap();

        dispatch(
          leaveRequestActions.setLeaveRequest({
            createdDate,
            requestId: reqId,
            reqStatusId,
          })
        );

        if (leaveTypeID === LeaveTypeCodes.SICK_LEAVE) {
          await uploadFile(String(reqId));
        }

        saveSuccessNotification(String(reqId));

        setDisableSaveForm(false);

        handleBackToListRequests();
      } catch (error) {
        const message = (error as RequestError)?.data;
        errorNotification(message);

        setDisableSaveForm(false);
      }
    };

    const saveAsDraftHandler = async (): Promise<void> => {
      const isFormValid = await validateForm(validationSchema, formik);

      if (!isFormValid) {
        return;
      }

      await saveAsDraft();
    };

    saveAsDraftHandler();
  }, [
    formik,
    handleBackToListRequests,
    uploadFile,
    currentFileUpload,
    currentUser,
    validationSchema,
  ]);

  useEffect(() => {
    if (formik.values.startDate && formik.values.endDate) {
      const totalDays = getTotalDays(
        formik.values.endDate,
        formik.values.startDate
      );

      formik.setFieldValue('totDays', totalDays);
    } else {
      formik.setFieldValue('totDays', '');
    }
  }, [formik.values.startDate, formik.values.endDate]);

  useEffect(() => {
    if (Number(formik.values.leaveTypeID) !== LeaveTypeCodes.ANNUAL_LEAVE) {
      formik.setFieldValue('ticketRequired', 0);
    }
  }, [formik.values.leaveTypeID]);

  useEffect(() => {
    if (!currentUser.empNumber) {
      return;
    }

    getBalance({ empNumber: currentUser.empNumber, date: '' })
      .unwrap()
      .then((balanceResp) => {
        setBalance({
          asOnToday: balanceResp.asOnToday,
          asOnDate: 0,
          leaveThisYear: balanceResp.leaveThisYear,
          ticketBalance: balanceResp.ticketBalance,
          ticketThisYear: balanceResp.ticketThisYear,
          eligibleTickets: balanceResp.eligibleTickets,
          annualLeaveThisYear: balanceResp.annualLeaveThisYear,
          emergencyLeaveThisYear: balanceResp.emergencyLeaveThisYear,
          sickLeaveThisYear: balanceResp.sickLeaveThisYear,
          isReplacementRequired: balanceResp.isReplacementRequired,
        });
        formik.setFieldValue(
          'isReplacementRequired',
          balanceResp.isReplacementRequired
        );
        setSkipGetReplacementUsers(!balanceResp.isReplacementRequired);
      })
      .catch(() => {});
  }, [currentUser]);

  useEffect(() => {
    if (formik.values.startDate && currentUser.empNumber) {
      const startDateFormatted = parseDateTime(
        formik.values.startDate,
        AVAILABLE_BALANCE_DATE_FORMAT
      );

      getBalance({ empNumber: currentUser.empNumber, date: startDateFormatted })
        .unwrap()
        .then((balanceResp) => {
          setBalance({
            ...balance,
            asOnDate: balanceResp.asOnDate,
            ticketBalance: balanceResp.ticketBalance,
            eligibleTickets: balanceResp.eligibleTickets,
          });
        })
        .catch(() => {});
    } else {
      setBalance({
        ...balance,
        asOnDate: 0,
        ticketBalance: 0,
        eligibleTickets: 0,
      });
    }
  }, [formik.values.startDate, currentUser]);

  const leaveTypeOptions = useMemo(
    () => transformDictToOptions(leaveTypes),
    [leaveTypes]
  );
  const passportOptions = useMemo(() => transformFlagsToOptions(), []);
  const ticketOptions = useMemo(() => transformFlagsToOptions(), []);

  const handleFooterAction = useCallback(
    (action: FooterActions) => {
      if (action === FooterActions.SAVE_AS_DRAFT) {
        handleSaveAsDraft();
      }

      if (action === FooterActions.BACK_TO_REQUESTS) {
        handleBackToListRequests();
      }
    },
    [handleBackToListRequests, handleSaveAsDraft]
  );

  return (
    <Flex gap={10} vertical style={{ height: '100%' }}>
      <MainForm
        handleSubmit={formik.handleSubmit}
        userInfo={currentUser}
        requisitionInfo={requisitionInfo}
        disabledSaveForm={disabledSaveForm}
        onFooterAction={handleFooterAction}
        descriptionAlert={descriptionAlert}
        showLeaveBalance
        balance={balance}
        formType="create"
      >
        <FormItems>
          <LeaveDetailsSection
            label="Leave Details"
            formik={formik}
            formApproval={false}
            leaveTypeOptions={leaveTypeOptions}
            passportOptions={passportOptions}
            ticketOptions={ticketOptions}
            onFileRequest={handleFileRequest}
            onFileRemove={handleFileRemove}
            balance={balance}
          />

          <ReplacementDetailsSection
            label="Replacement Details"
            formik={formik}
            listOfUsers={replacementUsers || []}
            isReplacementRequired={balance?.isReplacementRequired}
            onSearch={onSearch}
            onLoadMore={onLoadMore}
          />
        </FormItems>
      </MainForm>
    </Flex>
  );
};

export default LeaveRequestCreateForm;
